
import React from 'react';
import { Check, ShieldCheck, Trophy, Rocket, UserCheck, ShieldAlert } from 'lucide-react';

const Pricing: React.FC = () => {
  const scrollToForm = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="pricing" className="py-24 bg-[#0F1419] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#FF6B35] font-black uppercase tracking-[0.4em] text-sm mb-4 block">White Glove Setup + Coaching + Systems</span>
          <h2 className="text-5xl md:text-8xl font-black mb-6 uppercase text-white leading-[0.85] tracking-tighter italic">Cheaper Than A <br />Single Day Of Labor</h2>
          <p className="text-2xl text-[#1A4D2E] font-black italic max-w-3xl mx-auto uppercase">
            One booked job pays for the entire year.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-20">
          {/* Column 1: Implementation */}
          <div className="bg-white/5 border border-white/10 p-10 rounded-[2.5rem] relative group hover:border-[#1A4D2E] transition-colors">
            <div className="w-14 h-14 bg-[#1A4D2E]/20 rounded-2xl flex items-center justify-center mb-8">
              <Rocket className="w-8 h-8 text-[#1A4D2E]" />
            </div>
            <h3 className="text-2xl font-black text-white uppercase mb-4 italic leading-none tracking-tight">1. White Glove Implementation</h3>
            <p className="text-xs text-[#FF6B35] mb-8 font-black uppercase tracking-widest">Value: $2,500</p>
            <ul className="space-y-4 text-sm font-bold text-gray-300">
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-[#1A4D2E] flex-shrink-0" /> <span>We import your entire contact list</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-[#1A4D2E] flex-shrink-0" /> <span>Set up all automations FOR you</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-[#1A4D2E] flex-shrink-0" /> <span>Configure your booking calendar</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-[#1A4D2E] flex-shrink-0" /> <span>Integrate your existing tools</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-[#1A4D2E] flex-shrink-0" /> <span>Custom site templates for YOUR area</span></li>
            </ul>
          </div>

          {/* Column 2: Coaching */}
          <div className="bg-white/5 border-4 border-[#FF6B35]/20 p-10 rounded-[2.5rem] relative group hover:border-[#FF6B35] transition-colors shadow-2xl">
            <div className="w-14 h-14 bg-[#FF6B35]/20 rounded-2xl flex items-center justify-center mb-8">
              <UserCheck className="w-8 h-8 text-[#FF6B35]" />
            </div>
            <h3 className="text-2xl font-black text-white uppercase mb-4 italic leading-none tracking-tight">2. Coaching & Accountability</h3>
            <p className="text-xs text-[#FF6B35] mb-8 font-black uppercase tracking-widest">Value: $5,000/year</p>
            <ul className="space-y-4 text-sm font-bold text-gray-300">
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-[#FF6B35] flex-shrink-0" /> <span>Weekly group owner coaching calls</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-[#FF6B35] flex-shrink-0" /> <span>Monthly 1-on-1 strategy sessions</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-[#FF6B35] flex-shrink-0" /> <span>Access to our private community</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-[#FF6B35] flex-shrink-0" /> <span>Real-time support when stuck</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-[#FF6B35] flex-shrink-0" /> <span>We track your numbers with you</span></li>
            </ul>
          </div>

          {/* Column 3: Systems */}
          <div className="bg-white/5 border border-white/10 p-10 rounded-[2.5rem] relative group hover:border-yellow-500 transition-colors">
            <div className="w-14 h-14 bg-yellow-500/20 rounded-2xl flex items-center justify-center mb-8">
              <Trophy className="w-8 h-8 text-yellow-500" />
            </div>
            <h3 className="text-2xl font-black text-white uppercase mb-4 italic leading-none tracking-tight">3. Proven Trade Systems</h3>
            <p className="text-xs text-[#FF6B35] mb-8 font-black uppercase tracking-widest">Value: $10,000+</p>
            <ul className="space-y-4 text-sm font-bold text-gray-300">
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-yellow-500 flex-shrink-0" /> <span>Field-tested follow-up sequences</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-yellow-500 flex-shrink-0" /> <span>Pricing scripts & quote frameworks</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-yellow-500 flex-shrink-0" /> <span>Seasonal marketing calendars</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-yellow-500 flex-shrink-0" /> <span>Hiring/crew management SOPs</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-yellow-500 flex-shrink-0" /> <span>Full 7-figure scaling playbook</span></li>
            </ul>
          </div>
        </div>

        {/* Final Investment Stack */}
        <div className="max-w-5xl mx-auto relative group">
          <div className="absolute -inset-2 bg-gradient-to-r from-[#1A4D2E] via-[#FF6B35] to-[#4E3629] rounded-[4rem] blur-2xl opacity-30 group-hover:opacity-50 transition-opacity" />
          <div className="relative bg-[#F4E4C1] text-[#0F1419] p-8 md:p-20 rounded-[3.5rem] shadow-2xl text-center border-4 border-[#4E3629]/10">
            <div className="mb-10">
              <p className="text-sm font-black uppercase tracking-[0.3em] text-[#4E3629] mb-4">TOTAL VALUE: $17,500+</p>
              <h3 className="text-5xl md:text-8xl font-black uppercase leading-none italic tracking-tighter mb-4">
                $1,000 SETUP <br />
                <span className="text-[#FF6B35]">+</span> $297/MO
              </h3>
              <p className="text-xl font-black italic text-[#1A4D2E]">14-Day Free Trial Before Setup Is Charged</p>
            </div>

            {/* Guarantee Box */}
            <div className="bg-white p-8 md:p-12 rounded-3xl border-8 border-[#1A4D2E] text-left mb-16 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-10 opacity-5 pointer-events-none">
                <ShieldCheck className="w-40 h-40 text-[#1A4D2E]" />
              </div>
              <div className="flex items-center gap-6 mb-8">
                <ShieldCheck className="w-16 h-16 text-[#1A4D2E] flex-shrink-0" />
                <h4 className="text-3xl font-black uppercase leading-none italic tracking-tighter">The "We Work For Free Until You Win" Guarantee</h4>
              </div>
              <div className="text-lg font-bold leading-relaxed space-y-4 text-[#4E3629]">
                <p>"We'll set up your entire system, train you and your team, coach you through implementation, and work with you for <span className="bg-[#1A4D2E] text-white px-2">FREE until you book your first job</span> through the system."</p>
                <p>If we don't help you book at least <span className="underline decoration-[#FF6B35] decoration-4">ONE job worth more than the $1,000 setup fee</span> in the first 30 days, we'll refund every penny AND give you 60 days free. No contracts. Cancel anytime.</p>
              </div>
            </div>

            <div className="flex flex-col items-center gap-4">
              <button 
                onClick={scrollToForm}
                className="w-full bg-[#FF6B35] hover:bg-[#0F1419] text-white hover:text-white text-3xl md:text-5xl font-black py-10 rounded-3xl transition-all shadow-2xl transform active:scale-[0.98] uppercase tracking-tighter cursor-pointer"
              >
                SECURE MY SETUP SLOT
              </button>
              <p className="text-[#FF6B35] font-black uppercase text-sm tracking-widest animate-pulse">
                ONLY 7 SPOTS LEFT THIS MONTH
              </p>
            </div>
            <p className="mt-8 text-sm text-[#4E3629] font-black italic uppercase tracking-widest opacity-60">
              "This Isn't Software - It's A Partnership"
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
